import {
  Breadcrumbs,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Link as MuiLink,
  Divider,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import PageLoader from "../app/PageLoader";
import { useSelector } from "react-redux";
import { AppState } from "../../redux/reducer/rootReducer";
import { useGetDMSManufacturersBySetupIdQuery } from "../../redux/reducer/features/Integration/DmsSetupSlice";
import PortalManufacturerItem from "./Common/PortalManufacturerItem";
import { StyledBreadcrumb } from "./EmbeddedParts";
import FactoryIcon from "@mui/icons-material/Factory";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { DmsCategory } from "../../models/features/ShoppingPortal/DmsManufacturer";
import { useGetDmsCategoriesMutation } from "../../redux/reducer/features/ShoppingPortal/ShoppingPortalSlice";

declare const emergepay: any;
export const PortalCategories: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const setupId = useSelector(
    (state: AppState) =>
      state.shoppingPortalReducer.portalSettings?.setting.dmsSetupId
  );
  const storeUrl = useSelector(
    (state: AppState) => state.shoppingPortalReducer.storeUrl
  );

  const dmsLocations = useSelector(
    (state: AppState) =>
      state.shoppingPortalReducer.portalSettings?.dmsLocations
  );
  const dmsDealerId = useSelector(
    (state: AppState) => state.shoppingPortalReducer.portalSettings?.dmsDealerId
  );
  const [filteredCategories, setFilteredCategories] = useState<DmsCategory[]>(
    []
  );

  const [dmsCategoriesMutation, dmsCategoriesMutationResult] =
    useGetDmsCategoriesMutation();

  const groupedCategories: { [key: string]: DmsCategory[] } =
    filteredCategories.reduce((acc, category) => {
      const firstLetter = category.description.charAt(0).toUpperCase();
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter].push(category);
      return acc;
    }, {} as { [key: string]: DmsCategory[] });

  const handleGetDmsCategories = () => {
    dmsCategoriesMutation({
      dealerIds: dmsLocations
        ? dmsLocations.map((n) => n.dealerId.toString())
        : [],
    }).then((response: any) => {
      if (response.data && response.data.status === 200) {
        let data = response.data.entityList.filter((cat: any) => cat.isActive);

        setFilteredCategories(data);
      }
    });
  };

  React.useEffect(() => {
    if (dmsLocations !== undefined && dmsDealerId !== undefined) {
      handleGetDmsCategories();
    }
  }, [dmsDealerId]);

  // Sort each group alphabetically
  Object.keys(groupedCategories).forEach((letter) => {
    groupedCategories[letter].sort((a, b) =>
      a.description.localeCompare(b.description)
    );
  });

  // Create a ref object to hold refs for each letter section
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Initialize refs for each letter
  Object.keys(groupedCategories).forEach((letter) => {
    if (!sectionRefs.current[letter]) {
      sectionRefs.current[letter] = null;
    }
  });

  // Scroll to the section when a letter is clicked
  const handleLetterClick = (letter: string) => {
    const ref = sectionRefs.current[letter];
    if (ref) {
      ref.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleCategoryClick = (category: DmsCategory) => {
    navigate(
      "/ShoppingPortal/EmbeddedParts" +
        window.location.search +
        `&categoryId=${category.categoryId}&catName=${category.description}&view=grid`
    );
  };

  const activeCategoryFilter = useSelector(
    (state: AppState) =>
      state.shoppingPortalReducer.portalSettings?.setting?.activeCategoryFilter
  );
  const activeManufacturerFilter = useSelector(
    (state: AppState) =>
      state.shoppingPortalReducer.portalSettings?.setting
        ?.activeManufacturerFilter
  );
  return (
    <>
      <Container maxWidth="xl">
        <PageLoader
          isLoading={dmsCategoriesMutationResult.isLoading}
        ></PageLoader>
        <Grid container>
          <Grid item md={12}>
            <Box minHeight={100} bgcolor={"#f3f3f3"}>
              <Grid container md={12}>
                <Grid p={5} md={6} item>
                  <Typography variant="h4" fontWeight="bold">
                    Part Finder
                  </Typography>
                </Grid>
                <Grid md={6} item p={5}>
                  <Box sx={{ display: "flex", gap: 2, float: "right" }}>
                    Filter By:
                    {activeManufacturerFilter && (
                      <MuiLink
                        onClick={() =>
                          navigate(
                            `/ShoppingPortal/PartsByManuacturer?store=${storeUrl}`
                          )
                        }
                        color="inherit"
                        underline="none"
                        style={{ cursor: "pointer" }}
                      >
                        <strong>Manufacturer</strong>
                      </MuiLink>
                    )}
                    {activeCategoryFilter && (
                      <MuiLink
                        onClick={() => {
                          navigate(
                            `/ShoppingPortal/PartsByCategory?store=${storeUrl}`
                          );
                        }}
                        color="inherit"
                        underline="none"
                        style={{ cursor: "pointer" }}
                      >
                        <strong>Category</strong>
                      </MuiLink>
                    )}
                    <MuiLink
                      onClick={() => {
                        navigate(
                          `/ShoppingPortal/EmbeddedParts?store=${storeUrl}`
                        );
                      }}
                      color="inherit"
                      underline="none"
                      style={{ cursor: "pointer" }}
                    >
                      <strong>Search Filters</strong>
                    </MuiLink>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item md={12} mt={2}>
            <Breadcrumbs aria-label="breadcrumb">
              <StyledBreadcrumb
                label="Categories"
                icon={<FactoryIcon fontSize="small" />}
              />
            </Breadcrumbs>
          </Grid>
          <Grid item md={12}>
            <Grid container mt={2} mb={5} spacing={3} md={12}>
              <Box sx={{ padding: "20px", width: "100%", margin: "0 auto" }}>
                <Typography variant="h4" gutterBottom>
                  Choose a Part Category Below
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "10px",
                  }}
                >
                  {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((letter) => (
                    <Typography
                      key={letter}
                      sx={{
                        margin: "0 5px",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                      variant="body1"
                      onClick={() => handleLetterClick(letter)}
                    >
                      {letter}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="body1" gutterBottom align="center">
                  Select a letter above to see the Part Categories starting with
                  that letter.
                </Typography>
                {Object.keys(groupedCategories)
                  .sort()
                  .map((letter) => (
                    <Box
                      key={letter}
                      sx={{ marginBottom: "20px" }}
                      ref={(el: any) => (sectionRefs.current[letter] = el)}
                    >
                      <Typography variant="h5" id={`#${letter}`}>
                        {letter}
                      </Typography>
                      <Divider
                        sx={{
                          marginBottom: "10px",
                          backgroundColor: "#ed6c02",
                        }}
                      />
                      <Grid container spacing={2}>
                        {groupedCategories[letter].map((category) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            key={category.categoryId}
                          >
                            <Typography
                              variant="body1"
                              onClick={() => handleCategoryClick(category)}
                              style={{ cursor: "pointer" }}
                            >
                              {category.description}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export const searchOptions = {
  types: ["address"],
};
